/* eslint-disable max-lines */
import DesignTokensPreview from '@pages/design-tokens-preview.tsx';
import AdminWrapper from '@components/layout/admin-wrapper.tsx';
import BackOfficeWrapper from '@components/layout/back-office-wrapper.tsx';
import FeatureGuard from '@components/layout/feature-guard.tsx';
import PaymentPageWrapper from '@components/layout/payment-page-wrapper.tsx';
import PrivatePageWrapper from '@components/layout/private-page-wrapper.tsx';
import PublicPageWrapper from '@components/layout/public-page-wrapper.tsx';
import SharePageWrapper from '@components/layout/share-page-wrapper.tsx';
import ActivateUserForm from '@components/workspace/activate-user-form.tsx';
import FolderContents from '@components/workspace/folder-contents.tsx';
import BillableDetailsBo from '@pages/back-office/billable-details.tsx';
import CustomerBoDetails from '@pages/back-office/customer-bo-details.tsx';
import CustomerBoProfile from '@pages/back-office/customer-sections/customer-bo-profile.tsx';
import CustomerBoTokensPage from '@pages/back-office/customer-sections/customer-bo-tokens-page.tsx';
import CustomerBoSubscriptionsPage from '@pages/back-office/customer-sections/customer-bo-subscriptions-page.tsx';
import CustomerBoMandatesPage from '@pages/back-office/customer-sections/customer-bo-mandates-page.tsx';
import CustomerBoIamPage from '@pages/back-office/customer-sections/customer-bo-iam-page.tsx';
import CustomerBoActivityPage from '@pages/back-office/customer-sections/customer-bo-activity-page.tsx';
import CustomerBoWebhooksPage from '@pages/back-office/customer-sections/customer-bo-webhooks-page.tsx';
import CustomerBoTemplatesPage from '@pages/back-office/customer-sections/customer-bo-templates-page.tsx';
import CustomerBoExceedancesPage from '@pages/back-office/customer-sections/customer-bo-exceedances-page.tsx';
import CustomerBoAiSummaryPage from '@pages/back-office/customer-sections/customer-bo-ai-summary-page.tsx';
import ExceedancesBo from '@pages/back-office/exceedances-bo.tsx';
import CustomersBo from '@pages/back-office/customers-bo.tsx';
import LanguageAndConfigurationDetails from '@pages/back-office/language-and-configuration-details.tsx';
import PlanDetailsAndResellerPublications from '@pages/back-office/plan-details-and-reseller-publications.tsx';
import PlansBo from '@pages/back-office/plans-bo.tsx';
import PricePlansBo from '@pages/back-office/price-plans-bo.tsx';
import UsageBoDetails from '@pages/back-office/usage-details-bo.tsx';
// New redesigned back-office pages
import DashboardBo from '@pages/back-office/dashboard-bo.tsx';
// On-prem admin pages
import AdminDashboard from '@pages/admin/dashboard.tsx';
import AdminOverview from '@pages/admin/overview.tsx';
import AdminOrganizations from '@pages/admin/organizations.tsx';
import AdminOrganizationDetail from '@pages/admin/organization-detail.tsx';
import AdminOperations from '@pages/admin/operations.tsx';
import AdminQueue from '@pages/admin/queue.tsx';
import AdminPlans from '@pages/admin/plans.tsx';
import AdminLanguages from '@pages/admin/languages.tsx';
import AdminTranslation from '@pages/admin/translation.tsx';
import AdminSettings from '@pages/admin/settings.tsx';
import UsageRevenueBo from '@pages/back-office/usage-revenue-bo.tsx';
import ResellerUsageDetail from '@pages/back-office/reseller-usage-detail.tsx';
import OperationsBo from '@pages/back-office/operations-bo.tsx';
import SessionInspector from '@pages/back-office/session-inspector.tsx';
import SettingsBo from '@pages/back-office/settings-bo.tsx';
import LicensingBo from '@pages/back-office/licensing-bo.tsx';
import Glossaries from '@pages/main/glossaries.tsx';
import GlossaryDetails from '@pages/main/glossary-details.tsx';
import Speakers from '@pages/main/speakers.tsx';
import Tags from '@pages/main/tags.tsx';
import ExportTemplateItems from '@pages/main/export-template-items.tsx';
import Caption from '@pages/editors/caption.tsx';
import SharedCaption from '@pages/editors/shared-caption.tsx';
import SharedTranscript from '@pages/editors/shared-transcript.tsx';
import Transcript from '@pages/editors/transcript.tsx';
import RealtimeEditor from '@pages/editors/realtime.tsx';
import LoginMicrosoft from '@pages/login-microsoft.tsx';
import Login from '@pages/login.tsx';
import OAuthCallback from '@pages/main/oauth-callback.tsx';
import ApiKeys from '@pages/main/api-keys.tsx';
import BillingAndSubscription from '@pages/main/billing-and-subscription.tsx';
import ChangePassword from '@pages/main/change-password.tsx';
import CustomModels from '@pages/main/custom-models.tsx';
import Home from '@pages/main/home.tsx';
import LanguageAndConfig from '@pages/main/language-and-config.tsx';
import Profile from '@pages/main/profile.tsx';
import RegisterWithSubscriptionPlan from '@pages/main/register-with-subscription-plan.tsx';
import Register from '@pages/main/register.tsx';
import ResellerCustomer from '@pages/main/reseller-customer.tsx';
import ResellerAllUsers from '@pages/main/reseller-users.tsx';
import Reseller from '@pages/main/reseller.tsx';
import ResellerDashboard from '@pages/main/reseller-dashboard.tsx';
import ResellerLanguageAccess from '@pages/main/reseller-language-access.tsx';
import SpeechToTextSessions from '@pages/main/speech-to-text-sessions.tsx';
import StatusPage from '@pages/main/status-page.tsx';
import LicensesPage from '@pages/main/licenses-page.tsx';
import AccessibilityStatement from '@pages/main/accessibility-statement.tsx';
import StrandedSessions from '@pages/main/stranded-sessions.tsx';
import PriorityQueue from '@pages/back-office/priority-queue.tsx';
import SttSessions from '@pages/main/stt-sessions.tsx';
import SubscriptionPlans from '@pages/main/subscription-plan-types.tsx';
import { SubscriptionsDetails } from '@pages/main/subscriptions-details.tsx';
import Teams from '@pages/main/teams.tsx';
import ThankYouRegister from '@pages/main/thank-you-register.tsx';
import { UpdateCustomModel } from '@pages/main/update-custom-model.tsx';
import Users from '@pages/main/users.tsx';
import Maintenance from '@pages/maintenance.tsx';
import NotFound from '@pages/not-found.tsx';
import ResetPasswordForm from '@pages/reset-password-form.tsx';
import ResetPassword from '@pages/reset-password.tsx';
import {
  IS_ON_PREM,
  REGISTRATION_ENABLED
} from '@utils/constants/app-constants.ts';
import { TokenExtension } from '@utils/extensions/token.extension.ts';
import { Roles } from '@utils/types/shared/base.ts';
import { Navigate, RouteObject, useRoutes } from 'react-router-dom';
import { PARAMS, PATHS } from './paths.ts';
import TranscriptSessions from '@pages/back-office/transcript-sessions.tsx';

type RouteWithRoles = {
  roles: Roles[] | null;
  /** When true, route is hidden in on-prem deployments (VITE_ON_PREM=true) */
  cloudOnly?: boolean;
  /** When true, route is only visible in on-prem deployments (VITE_ON_PREM=true) */
  onPremOnly?: boolean;
} & RouteObject;

const routes: RouteWithRoles[] = [
  {
    path: PATHS().HOME,
    element: (
      <PrivatePageWrapper>
        <Home />
      </PrivatePageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS().MAINTENANCE,
    element: <Maintenance />,
    roles: null
  },
  {
    path: PATHS().DESIGN_TOKENS,
    element: (
      <PrivatePageWrapper>
        <DesignTokensPreview />
      </PrivatePageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS(PARAMS.FOLDER_ID).FOLDER_CONTENT,
    element: (
      <PrivatePageWrapper>
        <FolderContents />
      </PrivatePageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS().LOGIN,
    element: (
      <PublicPageWrapper>
        <Login />
      </PublicPageWrapper>
    ),
    roles: null
  },
  {
    // Minimal page the OAuth popup lands on. No wrapper — we want to
    // avoid any provider that could redirect (ex: Auth) before the
    // popup can postMessage back to its opener.
    path: PATHS().OAUTH_CALLBACK,
    element: <OAuthCallback />,
    roles: null
  },
  {
    path: PATHS().LOGIN_MICROSOFT,
    element: (
      <PublicPageWrapper>
        <LoginMicrosoft />
      </PublicPageWrapper>
    ),
    roles: null,
    cloudOnly: true
  },
  {
    path: PATHS().RESET_PASSWORD,
    element: (
      <PublicPageWrapper>
        <ResetPassword />
      </PublicPageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS().PASSWORD_RESET,
    element: (
      <PublicPageWrapper>
        <ResetPasswordForm />
      </PublicPageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS().REGISTER,
    element: REGISTRATION_ENABLED ? (
      <PublicPageWrapper>
        <Register />
      </PublicPageWrapper>
    ) : (
      <Navigate to={PATHS().LOGIN} replace />
    ),
    roles: null,
    cloudOnly: true
  },
  {
    path: PATHS().STATUS,
    element: (
      <>
        <StatusPage />
      </>
    ),
    roles: null
  },
  {
    path: PATHS().LICENSES,
    element: <LicensesPage />,
    roles: null
  },
  {
    path: PATHS().ACCESSIBILITY,
    element: <AccessibilityStatement />,
    roles: null
  },
  {
    path: PATHS().REGISTER_WITH_SUBSCRIPTION,
    element: REGISTRATION_ENABLED ? (
      <SharePageWrapper>
        <RegisterWithSubscriptionPlan />
      </SharePageWrapper>
    ) : (
      <Navigate to={PATHS().LOGIN} replace />
    ),
    roles: null,
    cloudOnly: true
  },
  {
    path: PATHS().SUBSCRIBE,
    element: (
      <PaymentPageWrapper>
        <SubscriptionPlans />
      </PaymentPageWrapper>
    ),
    roles: null,
    cloudOnly: true
  },
  {
    path: PATHS(PARAMS.ORGANIZATION_ID, PARAMS.PRODUCT).THANK_YOU_REGISTER,
    element: (
      <PaymentPageWrapper>
        <ThankYouRegister />
      </PaymentPageWrapper>
    ),
    roles: null,
    cloudOnly: true
  },
  {
    path: PATHS().ACTIVATE_USER,
    element: (
      <PublicPageWrapper>
        <ActivateUserForm />
      </PublicPageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS().ACTIVATE_USER,
    element: (
      <PublicPageWrapper>
        <ResetPasswordForm />
      </PublicPageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS().CHANGE_PASSWORD,
    element: (
      <PublicPageWrapper>
        <ChangePassword />
      </PublicPageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS(PARAMS.SESSION_ID, PARAMS.DOCUMENT_ID).TRANSCRIPT,
    element: (
      <PrivatePageWrapper layoutType={'editor'}>
        <Transcript />
      </PrivatePageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS(PARAMS.SESSION_ID, PARAMS.DOCUMENT_ID, PARAMS.TOKEN)
      .TRANSCRIPT_SHARED,
    element: (
      <SharePageWrapper layoutType={'editor'}>
        <SharedTranscript />
      </SharePageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS(PARAMS.SESSION_ID, PARAMS.DOCUMENT_ID).CAPTIONS,
    element: (
      <PrivatePageWrapper layoutType={'editor'}>
        <Caption />
      </PrivatePageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS(PARAMS.SESSION_ID, PARAMS.DOCUMENT_ID, PARAMS.TOKEN)
      .CAPTION_SHARED,
    element: (
      <SharePageWrapper layoutType={'editor'}>
        <SharedCaption />
      </SharePageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS().REALTIME_EDITOR,
    element: (
      <PrivatePageWrapper layoutType={'editor'}>
        <RealtimeEditor />
      </PrivatePageWrapper>
    ),
    roles: null
  },
  {
    path: '/realtime/session/:sessionId',
    element: (
      <PrivatePageWrapper layoutType={'editor'}>
        <RealtimeEditor />
      </PrivatePageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS().PROFILE,
    element: (
      <PrivatePageWrapper>
        <Profile />
      </PrivatePageWrapper>
    ),
    roles: null
  },
  // ================================
  // BACK OFFICE - NEW NAVIGATION
  // ================================
  {
    path: PATHS().BO_DASHBOARD,
    element: (
      <BackOfficeWrapper>
        <DashboardBo />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS().CUSTOMERS,
    element: (
      <BackOfficeWrapper>
        <CustomersBo />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS().BO_USAGE_REVENUE,
    element: (
      <BackOfficeWrapper>
        <UsageRevenueBo />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: '/back-office/usage-revenue/reseller/:resellerId',
    element: (
      <BackOfficeWrapper>
        <ResellerUsageDetail />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS().BO_OPERATIONS,
    element: (
      <BackOfficeWrapper>
        <OperationsBo />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS().BO_SESSION_INSPECTOR,
    element: (
      <BackOfficeWrapper>
        <SessionInspector />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS().BO_SETTINGS,
    element: (
      <BackOfficeWrapper>
        <SettingsBo />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS().BO_LICENSING,
    element: (
      <BackOfficeWrapper>
        <LicensingBo />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS().BO_EXCEEDANCES,
    element: (
      <BackOfficeWrapper>
        <ExceedancesBo />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS(PARAMS.CUSTOMER_ID).CUSTOMER_CONTENT,
    element: (
      <BackOfficeWrapper>
        <CustomerBoDetails />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS(PARAMS.CUSTOMER_ID).CUSTOMER_PROFILE,
    element: (
      <BackOfficeWrapper>
        <CustomerBoProfile />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS(PARAMS.CUSTOMER_ID).CUSTOMER_TOKENS,
    element: (
      <BackOfficeWrapper>
        <CustomerBoTokensPage />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS(PARAMS.CUSTOMER_ID).CUSTOMER_SUBSCRIPTIONS,
    element: (
      <BackOfficeWrapper>
        <CustomerBoSubscriptionsPage />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS(PARAMS.CUSTOMER_ID).CUSTOMER_MANDATES,
    element: (
      <BackOfficeWrapper>
        <CustomerBoMandatesPage />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS(PARAMS.CUSTOMER_ID).CUSTOMER_IAM,
    element: (
      <BackOfficeWrapper>
        <CustomerBoIamPage />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS(PARAMS.CUSTOMER_ID).CUSTOMER_ACTIVITY,
    element: (
      <BackOfficeWrapper>
        <CustomerBoActivityPage />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS(PARAMS.CUSTOMER_ID).CUSTOMER_WEBHOOKS,
    element: (
      <BackOfficeWrapper>
        <CustomerBoWebhooksPage />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS(PARAMS.CUSTOMER_ID).CUSTOMER_TEMPLATES,
    element: (
      <BackOfficeWrapper>
        <CustomerBoTemplatesPage />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS(PARAMS.CUSTOMER_ID).CUSTOMER_EXCEEDANCES,
    element: (
      <BackOfficeWrapper>
        <CustomerBoExceedancesPage />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS(PARAMS.CUSTOMER_ID).CUSTOMER_AI_SUMMARY,
    element: (
      <BackOfficeWrapper>
        <CustomerBoAiSummaryPage />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS().PRICE_PLANS,
    element: (
      <BackOfficeWrapper>
        <PricePlansBo />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS().PLANS,
    element: (
      <BackOfficeWrapper>
        <PlansBo />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS(PARAMS.PLAN_ID).PLAN_DETAILS,
    element: (
      <BackOfficeWrapper>
        <PlanDetailsAndResellerPublications />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS().BILLABLES,
    element: (
      <BackOfficeWrapper>
        <BillableDetailsBo />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS().LANGUAGE_AND_CONFIGS,
    element: (
      <BackOfficeWrapper>
        <LanguageAndConfig />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS().GLOSSARIES,
    element: (
      <PrivatePageWrapper>
        <Glossaries />
      </PrivatePageWrapper>
    ),
    roles: [Roles.ORGADMIN, Roles.CONTRIBUTOR]
  },
  {
    path: PATHS(PARAMS.GLOSSARY_ID).GLOSSARY_DETAILS,
    element: (
      <PrivatePageWrapper>
        <GlossaryDetails />
      </PrivatePageWrapper>
    ),
    roles: [Roles.ORGADMIN, Roles.CONTRIBUTOR]
  },
  {
    path: PATHS().EXPORT_TEMPLATE_ITEMS,
    element: (
      <PrivatePageWrapper>
        <FeatureGuard feature="templates_export">
          <ExportTemplateItems />
        </FeatureGuard>
      </PrivatePageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS().USAGE,
    element: (
      <BackOfficeWrapper>
        <UsageBoDetails />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS().STRANDED_SESSIONS,
    element: (
      <BackOfficeWrapper>
        <StrandedSessions />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS().PRIORITY_QUEUE,
    element: (
      <BackOfficeWrapper>
        <PriorityQueue />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS().TRAFFIC_MONITORING,
    element: (
      <BackOfficeWrapper>
        <TranscriptSessions />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS().API_KEYS,
    element: (
      <PrivatePageWrapper>
        <ApiKeys />
      </PrivatePageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS().STT,
    element: (
      <PrivatePageWrapper>
        <SttSessions />
      </PrivatePageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS().SPEECH_TO_TEXT_SESSIONS,
    element: (
      <PrivatePageWrapper>
        <SpeechToTextSessions />
      </PrivatePageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS().BILLING_AND_SUBSCRIPTION,
    element: (
      <PrivatePageWrapper>
        <BillingAndSubscription />
      </PrivatePageWrapper>
    ),
    roles: null,
    cloudOnly: true
  },
  {
    path: PATHS().CUSTOM_MODELS,
    element: (
      <PrivatePageWrapper>
        <FeatureGuard feature="automatic_training">
          <CustomModels />
        </FeatureGuard>
      </PrivatePageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS(PARAMS.MODEL_ID).CUSTOM_MODEL,
    element: (
      <PrivatePageWrapper>
        <FeatureGuard feature="automatic_training">
          <UpdateCustomModel />
        </FeatureGuard>
      </PrivatePageWrapper>
    ),
    roles: null,
    cloudOnly: true
  },
  {
    path: PATHS(PARAMS.SUBSCRIPTION_ID).SUBSCRIPTION_CONTENT,
    element: (
      <PrivatePageWrapper>
        <SubscriptionsDetails />
      </PrivatePageWrapper>
    ),
    roles: null,
    cloudOnly: true
  },
  {
    path: PATHS().RESELLER,
    element: (
      <PrivatePageWrapper>
        <ResellerDashboard />
      </PrivatePageWrapper>
    ),
    roles: [Roles.RESELLER],
    cloudOnly: true
  },
  {
    path: PATHS().RESELLER_ORGANIZATIONS,
    element: (
      <PrivatePageWrapper>
        <Reseller />
      </PrivatePageWrapper>
    ),
    roles: [Roles.RESELLER],
    cloudOnly: true
  },
  {
    path: PATHS().RESELLER_USERS,
    element: (
      <PrivatePageWrapper>
        <ResellerAllUsers />
      </PrivatePageWrapper>
    ),
    roles: [Roles.RESELLER],
    cloudOnly: true
  },
  {
    path: PATHS().RESELLER_ALL_USERS,
    element: (
      <PrivatePageWrapper>
        <ResellerAllUsers />
      </PrivatePageWrapper>
    ),
    roles: [Roles.RESELLER],
    cloudOnly: true
  },
  {
    path: PATHS().RESELLER_LANGUAGE_ACCESS,
    element: (
      <PrivatePageWrapper>
        <ResellerLanguageAccess />
      </PrivatePageWrapper>
    ),
    roles: [Roles.RESELLER],
    cloudOnly: true
  },
  {
    path: PATHS(PARAMS.RESELLER_ID).RESELLER_CUSTOMER,
    element: (
      <PrivatePageWrapper>
        <ResellerCustomer />
      </PrivatePageWrapper>
    ),
    roles: [Roles.RESELLER],
    cloudOnly: true
  },
  {
    path: PATHS().USERS,
    element: (
      <PrivatePageWrapper>
        <Users />
      </PrivatePageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS(PARAMS.LANGUAGE_ID).LANGUAGE_AND_PUBLICATIONS,
    element: (
      <BackOfficeWrapper>
        <LanguageAndConfigurationDetails />
      </BackOfficeWrapper>
    ),
    roles: [Roles.SYSOP],
    cloudOnly: true
  },
  {
    path: PATHS().TEAMS,
    element: (
      <PrivatePageWrapper>
        <Teams />
      </PrivatePageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS().SPEAKERS,
    element: (
      <PrivatePageWrapper>
        <Speakers />
      </PrivatePageWrapper>
    ),
    roles: null
  },
  {
    path: PATHS().TAGS,
    element: (
      <PrivatePageWrapper>
        <Tags />
      </PrivatePageWrapper>
    ),
    roles: null
  },
  // ================================
  // ON-PREM ADMIN PANEL
  // ================================
  {
    path: PATHS().ADMIN_DASHBOARD,
    element: (
      <AdminWrapper>
        <AdminDashboard />
      </AdminWrapper>
    ),
    roles: [Roles.SYSOP, Roles.ORGADMIN],
    onPremOnly: true
  },
  {
    path: PATHS().ADMIN_OVERVIEW,
    element: (
      <AdminWrapper>
        <AdminOverview />
      </AdminWrapper>
    ),
    roles: [Roles.SYSOP, Roles.ORGADMIN],
    onPremOnly: true
  },
  {
    path: PATHS().ADMIN_ORGANIZATIONS,
    element: (
      <AdminWrapper>
        <AdminOrganizations />
      </AdminWrapper>
    ),
    roles: [Roles.SYSOP, Roles.ORGADMIN],
    onPremOnly: true
  },
  {
    path: PATHS(PARAMS.ORGANIZATION_ID).ADMIN_ORGANIZATION_DETAIL,
    element: (
      <AdminWrapper>
        <AdminOrganizationDetail />
      </AdminWrapper>
    ),
    roles: [Roles.SYSOP, Roles.ORGADMIN],
    onPremOnly: true
  },
  {
    path: PATHS().ADMIN_OPERATIONS,
    element: (
      <AdminWrapper>
        <AdminOperations />
      </AdminWrapper>
    ),
    roles: [Roles.SYSOP, Roles.ORGADMIN],
    onPremOnly: true
  },
  {
    path: PATHS().ADMIN_QUEUE,
    element: (
      <AdminWrapper>
        <AdminQueue />
      </AdminWrapper>
    ),
    roles: [Roles.SYSOP, Roles.ORGADMIN],
    onPremOnly: true
  },
  {
    path: PATHS().ADMIN_PLANS,
    element: (
      <AdminWrapper>
        <AdminPlans />
      </AdminWrapper>
    ),
    roles: [Roles.SYSOP, Roles.ORGADMIN],
    onPremOnly: true
  },
  {
    path: PATHS().ADMIN_LANGUAGES,
    element: (
      <AdminWrapper>
        <AdminLanguages />
      </AdminWrapper>
    ),
    roles: [Roles.SYSOP, Roles.ORGADMIN],
    onPremOnly: true
  },
  {
    path: PATHS().ADMIN_TRANSLATION,
    element: (
      <AdminWrapper>
        <AdminTranslation />
      </AdminWrapper>
    ),
    roles: [Roles.SYSOP, Roles.ORGADMIN],
    onPremOnly: true
  },
  {
    path: PATHS().ADMIN_SETTINGS,
    element: (
      <AdminWrapper>
        <AdminSettings />
      </AdminWrapper>
    ),
    roles: [Roles.SYSOP, Roles.ORGADMIN],
    onPremOnly: true
  },
  {
    path: PATHS().NOT_FOUND,
    element: (
      <PrivatePageWrapper>
        <NotFound />
      </PrivatePageWrapper>
    ),
    roles: null
  }
];

export default function Router() {
  // Logout user after 1 hour of inactivity
  // useInactivityLogout(3600000);
  // Filter routes based on user roles and on-prem mode
  const filteredRoutes: RouteObject[] = routes.filter(route => {
    // Filter out cloud-only routes when in on-prem mode
    if (IS_ON_PREM && route.cloudOnly) {
      return false;
    }

    // Filter out on-prem-only routes when in cloud mode
    if (!IS_ON_PREM && route.onPremOnly) {
      return false;
    }

    // Filter based on user roles
    return (
      route.roles === null ||
      TokenExtension.get()?.decoded?.scopes?.some(scope =>
        route.roles?.includes(scope)
      )
    );
  });

  return useRoutes(filteredRoutes);
}
